import { motion } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { GROUPS, SEDES, useLang } from "@/i18n";

export default function Schedule() {
  const { t } = useLang();
  const s = t.schedule;

  const daysLabel = (days) => days.map((d) => s.days[d]).join(` ${s.join} `);

  return (
    <section id="horarios" className="px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
        >
          {s.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
        >
          {s.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base"
        >
          {s.subtitle}
        </motion.p>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {SEDES.map((sede, si) => (
            <motion.div
              key={sede.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: si * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-card p-6 shadow-2xl shadow-black/80 transition-colors duration-300 hover:border-accent/60 sm:p-8"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/10 blur-[70px]" />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-lg font-extrabold uppercase tracking-tight sm:text-2xl">
                    {sede.name}
                  </h3>
                  <p className="mt-1 flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                    <MapPin size={13} className="text-accent" />
                    {sede.addr}
                  </p>
                  <a
                    data-testid={`schedule-maps-${sede.id}`}
                    href={sede.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent"
                  >
                    <MapPin size={11} /> {s.howTo}
                  </a>
                </div>
                <span className="shrink-0 rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                  {String(si + 1).padStart(2, "0")}
                </span>
              </div>

              <div className="mt-7 space-y-0">
                {GROUPS.filter((g) => g.sede === si).map((g) =>
                  g.status === "closed" ? (
                    <div
                      key={g.code}
                      data-testid="schedule-group-k3-closed"
                      className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-400/30 bg-amber-400/5 py-4 pl-3 pr-2"
                    >
                      <span className="rounded-md bg-amber-400/20 px-3 py-1.5 font-display text-sm font-extrabold uppercase text-amber-300">
                        {g.code}
                      </span>
                      <span className="flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-[0.12em] text-amber-300">
                        {s.k3Closed}
                      </span>
                    </div>
                  ) : (
                    <div
                      key={g.code}
                      data-testid={`schedule-group-${g.code.replace(".", "-").toLowerCase()}`}
                      className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 py-4"
                    >
                      <span className="rounded-md bg-accent/15 px-3 py-1.5 font-display text-sm font-extrabold uppercase text-accent">
                        {g.code}
                      </span>
                      <span className="text-sm font-semibold text-zinc-200">{daysLabel(g.days)}</span>
                      <span className="flex items-center gap-1.5 font-mono text-sm text-zinc-400">
                        <Clock size={14} className="text-accent" />
                        {g.time}
                      </span>
                    </div>
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
